/**
 * Real network transport for the upload manager (VistoeraTASK-00522).
 *
 * Image bytes keep going the existing, standard way - NEVER through
 * Render/Uvicorn when direct upload is on:
 *   direct_to_r2 = true : initiate (auth + session) -> browser PUT straight to
 *                         R2 with the presigned URL -> complete (HEAD-verify +
 *                         metadata). If the presigned URL expired (403) a fresh
 *                         one is requested for the SAME session/key.
 *   direct_to_r2 = false: the pre-existing proxy `/uploads/direct` (unchanged
 *                         product behaviour when R2 CORS is not configured).
 * Every request is pinned to the Workspace-scoped base URL captured when the
 * batch was queued (the shared client's default is reset when the page
 * unmounts) and carries the client upload id, so any retry is idempotent.
 */
import axios from 'axios'
import {
  completeUpload,
  initiateUpload,
  putFileToPresignedUrl,
  retryUploadPresign,
  uploadFileDirect,
  type UploadPlacementOpts,
} from '../../../api/client'
import type { UploadTransport } from './uploadManager'

function normalizeStorageError(err: unknown): Error {
  if (axios.isCancel(err)) return Object.assign(new Error('canceled'), { canceled: true })
  const e = err as { response?: { status?: number }; message?: string }
  return Object.assign(new Error(e?.message || 'Storage upload failed'), {
    status: e?.response?.status,
    source: 'storage',
  })
}

/** Content-Type must match the type signed into the presigned URL; wrap (lazily,
 * no byte copy) only when the browser reported a different/empty type. */
function withMime(file: File, mime: string): File {
  return file.type === mime ? file : new File([file], file.name, { type: mime })
}

export const defaultUploadTransport: UploadTransport = async (req, ctx) => {
  const scope = { baseURL: ctx.scopeBaseUrl, signal: ctx.signal }
  const opts: UploadPlacementOpts = {
    auto_process: ctx.autoProcess,
    asset_source: ctx.assetSource,
    collection_id: ctx.placement.collectionId,
    visibility: ctx.placement.visibility,
    upload_id: req.clientId,
  }
  const body = withMime(req.file, req.mime)

  if (!ctx.directToR2) {
    const { data } = await uploadFileDirect(body, ctx.onProgress, opts, scope)
    return { chainId: data.id ?? null }
  }

  const { data: init } = await initiateUpload(req.name, req.mime, req.size, opts, scope)
  if (!init.already_completed && init.upload_url) {
    const put = (url: string) =>
      putFileToPresignedUrl(url, body, ctx.onProgress, ctx.signal).catch((e) => {
        throw normalizeStorageError(e)
      })
    try {
      await put(init.upload_url)
    } catch (e) {
      // Expired / rejected signature -> mint a fresh URL for the SAME session.
      if ((e as { status?: number }).status !== 403) throw e
      const { data: fresh } = await retryUploadPresign(init.upload_id, scope)
      await put(fresh.upload_url)
    }
  }
  ctx.onPhase('finalizing')
  const { data: chain } = await completeUpload(init.upload_id, scope)
  return { chainId: chain.id ?? null }
}
